import {memo, useState, useEffect} from "react";
import {Edge, Handle, Position, useReactFlow} from "@xyflow/react";
import {WorkflowNodeData, WorkflowNodeTypes} from "@/types/node";
import {getTailwindColor} from "@/utils/index";
import {getSettingValueByKey} from "@/utils/index";
import NodeWithToolbar from "../components/NodeToolbar";
import useNode from "@/hooks/useNode";
import {WorkflowNodesData} from "./NodesData";
import {useSelectedElements} from "@/hooks/useSelectedElements";
const BranchNode = ({
  id,
  data,
  showContext,
}: {
  id: string;
  data: WorkflowNodeData;
  showContext: boolean;
}) => {
  const {deleteElements, getNode, getEdges} = useReactFlow();
  const {getSelectedNode} = useSelectedElements();
  const {addSiblingById} = useNode();
  const nodeConfig =
    WorkflowNodesData[data.type as keyof typeof WorkflowNodesData];

  const contextColor = getTailwindColor(nodeConfig.styles.contextColor);
  const nodeStyles = {
    icon: {
      color: nodeConfig.styles.iconColor,
      backgroundColor: nodeConfig.styles.iconBackgroundColor,
      borderColor: nodeConfig.styles.iconBorderColor,
    },
  };

  const branchName = getSettingValueByKey(data.settings ?? [], "name");
  const [isToolbarVisible, setIsToolbarVisible] = useState(false);
  const selectedNode = getSelectedNode();
  const isNodeSelected = selectedNode?.id === id;

  useEffect(() => {
    setIsToolbarVisible(isNodeSelected);
  }, [isNodeSelected]);

  return (
    <div
      onMouseEnter={() => setIsToolbarVisible(true)}
      onMouseLeave={() => !isNodeSelected && setIsToolbarVisible(false)}
      className={`w-85 flex justify-center ${showContext ? "enableContext" : ""}`}
      style={
        showContext
          ? ({
              "--overlay-color": contextColor,
            } as React.CSSProperties)
          : {}
      }
    >
      <div
        className={`branch-node px-4.5 py-1.5 bg-violet-lighter border border-violet rounded-full shadow-node transition-all max-w-full`}
      >
        <h5
          className={`text-s font-medium overflow-hidden text-ellipsis whitespace-nowrap hasContext ${branchName ? "text-violet-dark" : "text-violet-light"}`}
        >
          {branchName?.toString() || "Branch"}
        </h5>

        <NodeWithToolbar
          nodeId={id}
          nodeType={data.type}
          isToolbarVisible={isToolbarVisible}
          onDelete={() => {
            const edges = getEdges();
            const parentNodeId = edges.find(
              (edge: Edge) => edge.target === id,
            )?.source;
            const hasSiblings =
              edges.filter((edge: Edge) => edge.source === parentNodeId)
                .length > 1;

            // Find all edges connected to the node
            const edgesToDelete = edges.filter(
              (edge: Edge) => edge.source === id || edge.target === id,
            );

            // Find placeholders connected to the node
            const placeholders = edges
              .filter((edge: Edge) => edge.source === id)
              .map((edge: Edge) => getNode(edge.target))
              .filter(
                (node: any) =>
                  node && node.type === WorkflowNodeTypes.PLACEHOLDER,
              );

            if (hasSiblings) {
              deleteElements({
                nodes: [{id}, ...placeholders.map((p: any) => ({id: p.id}))],
                edges: edgesToDelete.map(edge => ({id: edge.id})),
              });
            } else {
              deleteElements({
                nodes: [{id}],
                edges: edgesToDelete.map(edge => ({id: edge.id})),
              });
            }
          }}
          onMouseEnter={() => setIsToolbarVisible(true)}
          onMouseLeave={() => isNodeSelected && setIsToolbarVisible(false)}
        />

        <Handle
          type="target"
          position={Position.Top}
          isConnectable={false}
          className={"handle"}
          id={`${id}-top-target`}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={false}
          className={"handle"}
          id={`${id}-bottom-source`}
        />
        <Handle
          type="source"
          position={Position.Right}
          isConnectable={true}
          className={"handle"}
          id={`${id}-right-source`}
        />
        <Handle
          type="source"
          position={Position.Left}
          isConnectable={true}
          className={"handle"}
          id={`${id}-left-source`}
        />
      </div>
    </div>
  );
};

export default memo(BranchNode);
